import { useEffect, useRef, useState } from "react";
import {
  getHandle,
  hasHandle,
  HandleModal,
  Leaderboard,
  ShareSheet,
  track,
  trackPageView,
  useLeaderboard,
  useRun,
} from "@karrtopelka/game-kit";
import type { CardProps } from "@karrtopelka/cards/types";
import { ODDS, type OutcomeKind } from "@karrtopelka/game-api/odds";

import { Gauge } from "./components/Gauge.js";
import { buildWheelCard } from "./lib/buildCard.js";
import { loadCardAssets } from "./lib/cardAssets.js";
import { terminalKind, useWheelStore } from "./store.js";

/** One shared leaderboard instance feeds the desktop rail AND the mobile board. */
type Board = ReturnType<typeof useLeaderboard>;

const SIBLINGS = [
  { name: "reflex", hook: "raw reaction time", href: "https://reflex.karrtopelka.com" },
  { name: "plinko", hook: "one ball, 470x", href: "https://plinko.karrtopelka.com" },
  { name: "mines", hook: "don't hit the bomb", href: "https://mines.karrtopelka.com" },
] as const;

export function App() {
  const board = useLeaderboard("wheel");
  const [handle, setHandle] = useState<string | null>(() => getHandle());

  return (
    <main
      className="relative flex min-h-screen min-h-[100dvh] flex-col items-center gap-6 px-6 text-text lg:grid lg:grid-cols-[minmax(200px,260px)_minmax(0,44rem)_minmax(240px,300px)] lg:grid-rows-[auto_1fr_auto] lg:items-start lg:justify-center lg:gap-10 lg:px-10"
      style={{
        paddingTop: "calc(2.5rem + env(safe-area-inset-top))",
        paddingBottom: "calc(2.5rem + env(safe-area-inset-bottom))",
      }}
    >
      <AmbientRing />

      <header
        className="flex animate-fade-up flex-col items-center lg:col-span-3 lg:justify-self-center"
        style={{ animationDelay: "0ms" }}
      >
        <h1 className="text-3xl font-extrabold tracking-tight lg:text-5xl">
          wheel<span className="animate-glow text-accent">.</span>
        </h1>
        <p className="text-sm text-text-muted lg:text-base">
          push your luck. biggest streak wins.
        </p>
      </header>

      {/* LEFT rail — the rules and the stake, quiet cards; the wheel is the star. */}
      <aside
        className="hidden animate-fade-up lg:sticky lg:top-8 lg:flex lg:flex-col lg:gap-4"
        style={{ animationDelay: "160ms" }}
      >
        <div className="rounded-lg border border-border bg-surface/60 p-4 backdrop-blur">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-text-muted">
            how it works
          </h2>
          <ul className="mt-2 flex flex-col gap-1.5 text-sm text-text-muted">
            <li>
              spin — land <span className="text-text">+1</span>, the streak grows
            </li>
            <li>
              <span className="text-accent">x2</span> / <span className="text-gold">x3</span>{" "}
              multiply the whole run
            </li>
            <li>
              <span className="text-danger">✕ bust</span> takes all of it. every time.
            </li>
          </ul>
        </div>

        <SessionCard />

        <div className="rounded-lg border border-border bg-surface/60 p-4 text-xs text-text-faint backdrop-blur">
          <kbd className="rounded border border-border bg-surface-raised px-1.5 py-0.5 font-mono text-[10px] text-text-muted">
            space
          </kbd>{" "}
          to spin
        </div>
      </aside>

      <Game board={board} handle={handle} onHandle={setHandle} />

      {/* RIGHT rail — the social proof lives beside the wheel, always visible. */}
      <aside
        className="hidden animate-fade-up lg:sticky lg:top-8 lg:flex lg:flex-col lg:gap-3"
        style={{ animationDelay: "240ms" }}
      >
        <div className="flex flex-col gap-3 rounded-lg border border-border bg-surface/60 p-4 backdrop-blur">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-text-muted">
            best streaks
          </h2>
          <Leaderboard
            entries={board.entries}
            loading={board.loading}
            myHandle={handle}
            formatValue={(v) => String(v)}
            emptyLabel="no streaks yet — go first."
            range={board.range}
            onRange={board.setRange}
          />
        </div>
      </aside>

      <footer
        className="mt-4 flex animate-fade-up flex-col items-center gap-1 text-xs text-text-faint lg:col-span-3 lg:mt-0 lg:w-full lg:self-end lg:justify-self-center"
        style={{ animationDelay: "320ms" }}
      >
        {/* desktop: more-games strip */}
        <div className="hidden w-full items-stretch justify-center gap-3 lg:flex">
          <span className="self-center text-[10px] uppercase tracking-widest text-text-faint">
            more games
          </span>
          {SIBLINGS.map((g) => (
            <a
              key={g.name}
              href={g.href}
              className="flex flex-col rounded-md border border-border bg-surface/60 px-4 py-2 backdrop-blur transition-all duration-200 hover:-translate-y-0.5 hover:border-accent hover:shadow-glow"
            >
              <span className="text-sm font-semibold text-text">
                {g.name}
                <span className="text-accent">.</span>
              </span>
              <span className="text-[11px] text-text-faint">{g.hook}</span>
            </a>
          ))}
          <a
            href="https://karrtopelka.com"
            className="self-center px-2 text-text-faint transition-colors duration-200 hover:text-accent"
          >
            karrtopelka.com →
          </a>
        </div>
        {/* mobile: tiny text links, as before */}
        <a
          href="https://reflex.karrtopelka.com"
          className="hover:text-accent hover:underline lg:hidden"
        >
          also try reflex — reaction test →
        </a>
        <a href="https://karrtopelka.com" className="hover:text-text-muted lg:hidden">
          karrtopelka.com
        </a>
      </footer>
    </main>
  );
}

/** Desktop-only session stats card — reads the same store the wheel writes. */
function SessionCard() {
  const best = useWheelStore((s) => s.best);
  const streak = useWheelStore((s) => s.streak);
  return (
    <div className="rounded-lg border border-border bg-surface/60 p-4 backdrop-blur">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-text-muted">
        this session
      </h2>
      <div className="mt-2 flex items-end justify-between">
        <div className="flex flex-col">
          <span className="font-display text-3xl font-extrabold tabular-nums text-accent">
            {best}
          </span>
          <span className="text-[10px] uppercase tracking-widest text-text-faint">
            your best
          </span>
        </div>
        <div className="flex flex-col items-end">
          <span
            className={`font-display text-3xl font-extrabold tabular-nums ${
              streak > 0 ? "text-danger" : "text-text-faint"
            }`}
          >
            {streak}
          </span>
          <span className="text-[10px] uppercase tracking-widest text-text-faint">
            on the line
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * Signature ambient motif: one huge, faint wheel-spoke ring drifting on a 48s
 * rotation, half off the right edge. Pure decoration — fixed, behind everything,
 * no pointer events, opacity kept under the contrast floor.
 */
function AmbientRing() {
  const spokes = Array.from({ length: 12 }, (_, i) => {
    const a = (i * 30 * Math.PI) / 180;
    return {
      x1: 100 + 20 * Math.cos(a),
      y1: 100 + 20 * Math.sin(a),
      x2: 100 + 94 * Math.cos(a),
      y2: 100 + 94 * Math.sin(a),
    };
  });
  return (
    <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
      <div className="absolute -right-[42vmin] top-1/2 h-[120vmin] w-[120vmin] -translate-y-1/2">
        <svg
          className="h-full w-full animate-spin-slow opacity-[0.06]"
          viewBox="0 0 200 200"
          fill="none"
          stroke="#ff6fa5"
        >
          <circle cx={100} cy={100} r={95} strokeWidth={0.5} />
          <circle cx={100} cy={100} r={71} strokeWidth={0.3} />
          <circle cx={100} cy={100} r={19} strokeWidth={0.5} />
          {spokes.map((s, i) => (
            <line key={i} x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2} strokeWidth={0.4} />
          ))}
        </svg>
      </div>
    </div>
  );
}

function Game({
  board,
  handle,
  onHandle,
}: {
  board: Board;
  handle: string | null;
  onHandle: (handle: string) => void;
}) {
  const run = useRun("wheel");

  const phase = useWheelStore((s) => s.phase);

  // Funnel beacons: one page_view per load, one play_start per session, one
  // play_done per landed spin.
  useEffect(() => trackPageView("wheel"), []);
  const startedTracking = useRef(false);
  useEffect(() => {
    if (phase === "spinning" && !startedTracking.current) {
      startedTracking.current = true;
      track("wheel", "play_start");
    }
    if (phase === "result") track("wheel", "play_done");
  }, [phase]);
  const streak = useWheelStore((s) => s.streak);
  const outcomes = useWheelStore((s) => s.outcomes);
  const lastResult = useWheelStore((s) => s.lastResult);
  const prevStreak = useWheelStore((s) => s.prevStreak);
  const best = useWheelStore((s) => s.best);
  const setStreak = useWheelStore((s) => s.setStreak);
  const beginSpin = useWheelStore((s) => s.beginSpin);
  const land = useWheelStore((s) => s.land);
  const toIdle = useWheelStore((s) => s.toIdle);

  const [showHandle, setShowHandle] = useState(false);
  const [card, setCard] = useState<CardProps | null>(null);
  const [spinError, setSpinError] = useState<string | null>(null);

  // Bridge the run hook's streak into the store on load — but don't clobber an
  // in-progress spin/result.
  useEffect(() => {
    if (run.loading) return;
    const p = useWheelStore.getState().phase;
    if (p === "spinning" || p === "result") return;
    setStreak(run.streak);
  }, [run.loading, run.streak, setStreak]);

  const doSpin = async () => {
    setSpinError(null);
    setCard(null);
    beginSpin();
    const res = await run.spin();
    if (!res) {
      setSpinError("couldn't spin — try again");
      toIdle();
      return;
    }
    // Gauge replays res.outcomes, then calls onLanded → land(res).
    useWheelStore.setState({ outcomes: res.outcomes, lastResult: res });
  };

  const onLanded = () => {
    const res = useWheelStore.getState().lastResult;
    if (!res) return;
    land(res);
    const who = getHandle() ?? "you";
    const kind = terminalKind(res.outcomes);
    const lostStreak = useWheelStore.getState().prevStreak;
    // Offer a share on a bust only if the lost streak was meaningful (the L is the joke).
    const shareable = kind !== "bust" || lostStreak >= 5;
    setCard(shareable ? buildWheelCard(res.streak, who, res, kind, lostStreak) : null);
    board.refresh();
  };

  const onSpinClick = () => {
    if (hasHandle()) void doSpin();
    else setShowHandle(true);
  };

  // Desktop affordance: spacebar/enter spins. Skips focused controls (so the
  // native button/input behavior never double-fires) and non-actionable phases.
  const spinClickRef = useRef(onSpinClick);
  spinClickRef.current = onSpinClick;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== " " && e.key !== "Enter") return;
      if (e.repeat) return;
      const t = e.target;
      if (
        t instanceof HTMLElement &&
        (t.tagName === "BUTTON" || t.tagName === "INPUT" || t.tagName === "TEXTAREA")
      )
        return;
      const p = useWheelStore.getState().phase;
      if (p !== "idle" && p !== "result") return;
      e.preventDefault();
      spinClickRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const kind = terminalKind(outcomes);
  const busted = phase === "result" && kind === "bust";
  const bigWin = phase === "result" && (kind === "x2" || kind === "jackpot");
  const grew = phase === "result" && !busted && streak > prevStreak;

  const spinBtn = (label: string) => (
    <button
      type="button"
      onClick={onSpinClick}
      disabled={phase === "spinning"}
      className="animate-neon-pulse rounded-md bg-accent px-8 py-3 text-base font-semibold text-bg transition-all duration-200 enabled:hover:-translate-y-0.5 enabled:hover:brightness-110 active:scale-[0.98] disabled:animate-none disabled:opacity-40 lg:px-10 lg:text-lg"
    >
      {phase === "spinning" ? "spinning…" : label}
    </button>
  );

  const top = board.entries[0] ?? null;
  const chasing = top !== null && top.value > streak;

  return (
    <div
      className="flex w-full max-w-md animate-fade-up flex-col items-center gap-6 lg:max-w-xl lg:justify-self-center"
      style={{ animationDelay: "80ms" }}
    >
      <Gauge
        spinning={phase === "spinning"}
        outcomes={outcomes}
        streak={streak}
        onLanded={onLanded}
      />

      {/* the stake: loss aversion does the selling */}
      {phase === "idle" && streak > 0 && (
        <p className="rounded-full border border-danger/40 bg-danger/10 px-4 py-1.5 text-sm font-semibold text-danger lg:text-base">
          🔥 {streak} on the line — one bust takes it all
        </p>
      )}

      {/* the chase */}
      <div className="flex items-center gap-4 text-xs text-text-faint lg:text-sm">
        <span>
          your best{" "}
          <span className="font-display text-sm text-accent lg:text-base">{best}</span>
        </span>
        {top &&
          (chasing ? (
            <span>
              👑 #1 is{" "}
              <span className="font-display text-sm text-gold lg:text-base">{top.value}</span>{" "}
              by {top.handle}
            </span>
          ) : (
            <span className="text-gold">👑 you're sitting on #1 territory</span>
          ))}
      </div>

      <OddsStrip />

      {phase === "loading" && <p className="text-sm text-text-faint">loading…</p>}

      {phase === "idle" && (
        <div className="flex flex-col items-center gap-2">
          {spinBtn(streak > 0 ? "spin again" : "spin")}
          {streak > 0 && (
            <p className="text-xs text-text-faint">push for more — or quit while ahead</p>
          )}
          {spinError && <p className="text-sm text-accent">{spinError}</p>}
        </div>
      )}

      {/* result */}
      {phase === "result" && (
        <div className="flex w-full flex-col items-center gap-5">
          {busted ? (
            <section className="flex flex-col items-center">
              <span className="mb-1 animate-shake text-lg font-bold uppercase tracking-wide text-danger lg:text-3xl">
                💥 busted
              </span>
              <span className="text-sm text-text-muted lg:text-base">
                lost your {prevStreak}-day run — back to 0
              </span>
            </section>
          ) : (
            <section className="flex flex-col items-center">
              {bigWin && (
                <span className="mb-1 animate-pop rounded-full bg-accent px-3 py-1 text-xs font-bold uppercase tracking-wide text-bg lg:px-4 lg:text-sm">
                  {kind === "jackpot" ? "🎰 x3 JACKPOT" : "✦ doubled"}
                </span>
              )}
              {!bigWin && grew && (
                <span className="mb-1 text-sm font-semibold text-accent lg:text-lg">
                  streak {streak} 🔥
                </span>
              )}
              {lastResult?.total !== undefined && lastResult.total >= 10 && (
                <p className="text-sm text-text lg:text-base">
                  best run #{lastResult.rank} of {lastResult.total}
                  <span className="text-accent"> · top {lastResult.percentile}%</span>
                </p>
              )}
            </section>
          )}

          {spinBtn(busted ? "spin (new run)" : "spin again")}

          {card && (
            <ShareSheet
              card={card}
              replyLink="wheel.karrtopelka.com/r"
              caption={
                busted
                  ? `lost my ${prevStreak} run. ${card.provocation}`
                  : `${streak} streak. ${card.provocation}`
              }
              loadAssets={loadCardAssets}
            />
          )}
        </div>
      )}

      {/* leaderboard — mobile only; on lg the right rail owns it */}
      <section className="flex w-full max-w-md flex-col items-center gap-3 border-t border-border pt-6 lg:hidden">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-text-muted">
          biggest streaks
        </h2>
        <Leaderboard
          entries={board.entries}
          loading={board.loading}
          myHandle={handle}
          formatValue={(v) => String(v)}
          emptyLabel="no streaks yet — go first."
          range={board.range}
          onRange={board.setRange}
        />
      </section>

      {showHandle && (
        <HandleModal
          onCancel={() => setShowHandle(false)}
          onDone={(h) => {
            setShowHandle(false);
            onHandle(h);
            void doSpin();
          }}
        />
      )}
    </div>
  );
}

const ODDS_CHIP: Record<OutcomeKind, { label: string; cls: string; desc: string }> = {
  "+1": { label: "+1", cls: "text-text-muted", desc: "+1 — streak grows, keep riding" },
  bust: { label: "✕ bust", cls: "text-danger", desc: "bust — the whole streak, gone" },
  x2: { label: "x2", cls: "text-accent", desc: "x2 — streak doubles" },
  nothing: { label: "whiff", cls: "text-text-faint", desc: "whiff — nothing happens" },
  jackpot: { label: "x3", cls: "text-gold", desc: "x3 jackpot — streak triples" },
  again: { label: "↻", cls: "text-text-faint", desc: "respin — go again, free" },
};

/** The house shows its hand: flat odds, always visible — the trust signal IS the hook. */
function OddsStrip() {
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="flex flex-wrap items-center justify-center gap-1.5">
        {ODDS.map((o) => (
          <span
            key={o.kind}
            title={ODDS_CHIP[o.kind].desc}
            className={`cursor-default rounded-full border border-border px-2 py-0.5 font-mono text-[11px] transition-colors duration-200 hover:border-accent/60 ${ODDS_CHIP[o.kind].cls} lg:px-2.5 lg:text-xs`}
          >
            {ODDS_CHIP[o.kind].label} {o.percent}%
          </span>
        ))}
      </div>
      <span className="text-[10px] text-text-faint lg:text-xs">
        flat odds · every spin independent · no rigging
      </span>
    </div>
  );
}
